import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  activateDocument,
  archiveDocument,
  createCorpus,
  fetchNodes,
  getStructure,
  ingestMarkdown,
  listCorpora,
  listDocuments,
  type FetchedNode,
  type KnowledgeCorpus,
  type KnowledgeDocMeta,
  type StructureNode,
} from '../api/knowledge'
import { ApiError } from '../api/auth'

type KnowledgeContextValue = {
  corpora: KnowledgeCorpus[]
  corpusId: string | null
  docs: KnowledgeDocMeta[]
  loading: boolean
  pending: boolean
  error: string | null
  selectCorpus: (id: string) => void
  refresh: () => Promise<void>
  getDoc: (docId: string) => KnowledgeDocMeta | undefined
  getStructureCached: (docId: string) => Promise<StructureNode[]>
  fetchNode: (docId: string, selector: string) => Promise<FetchedNode[]>
  addCorpus: (body: { slug: string; name: string }) => Promise<KnowledgeCorpus>
  addDocument: (body: {
    slug: string
    markdown: string
    activate: boolean
  }) => Promise<KnowledgeDocMeta>
  activate: (docId: string) => Promise<void>
  archive: (docId: string) => Promise<void>
}

const KnowledgeContext = createContext<KnowledgeContextValue | null>(null)

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'request_failed'
}

/** Loaded once per session and kept warm across list/detail navigation —
 * same "fetch once, cache in context" shape as `TasksProvider`. */
export function KnowledgeProvider({ children }: { children: ReactNode }) {
  const [corpora, setCorpora] = useState<KnowledgeCorpus[]>([])
  const [corpusId, setCorpusId] = useState<string | null>(null)
  const [docsByCorpus, setDocsByCorpus] = useState<Map<string, KnowledgeDocMeta[]>>(
    new Map(),
  )
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const structureCache = useRef<Map<string, StructureNode[]>>(new Map())

  const loadDocs = useCallback(async (cid: string) => {
    const list = await listDocuments(cid)
    setDocsByCorpus((prev) => new Map(prev).set(cid, list))
    return list
  }, [])

  const refresh = useCallback(async () => {
    setPending(true)
    setError(null)
    try {
      const list = await listCorpora()
      setCorpora(list)
      const nextId = corpusId && list.some((c) => c.id === corpusId)
        ? corpusId
        : list[0]?.id ?? null
      setCorpusId(nextId)
      if (nextId) await loadDocs(nextId)
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setPending(false)
      setLoading(false)
    }
  }, [corpusId, loadDocs])

  const bootstrapped = useRef(false)
  if (!bootstrapped.current) {
    bootstrapped.current = true
    void refresh()
  }

  const selectCorpus = useCallback(
    (id: string) => {
      setCorpusId(id)
      if (!docsByCorpus.has(id)) {
        setPending(true)
        loadDocs(id)
          .catch((err) => setError(messageFor(err)))
          .finally(() => setPending(false))
      }
    },
    [docsByCorpus, loadDocs],
  )

  const docs = useMemo(
    () => (corpusId ? docsByCorpus.get(corpusId) ?? [] : []),
    [corpusId, docsByCorpus],
  )

  const getDoc = useCallback(
    (docId: string) => {
      for (const list of docsByCorpus.values()) {
        const hit = list.find((d) => d.doc_id === docId)
        if (hit) return hit
      }
      return undefined
    },
    [docsByCorpus],
  )

  const getStructureCached = useCallback(async (docId: string) => {
    const cached = structureCache.current.get(docId)
    if (cached) return cached
    const res = await getStructure(docId)
    structureCache.current.set(docId, res.structure)
    return res.structure
  }, [])

  const fetchNode = useCallback(async (docId: string, selector: string) => {
    const res = await fetchNodes(docId, selector)
    return res.nodes
  }, [])

  const patchDoc = useCallback((updated: KnowledgeDocMeta) => {
    setDocsByCorpus((prev) => {
      const next = new Map(prev)
      for (const [cid, list] of next) {
        const idx = list.findIndex((d) => d.doc_id === updated.doc_id)
        if (idx !== -1) {
          const copy = [...list]
          copy[idx] = updated
          next.set(cid, copy)
        }
      }
      return next
    })
  }, [])

  const addCorpus = useCallback(
    async (body: { slug: string; name: string }) => {
      const c = await createCorpus(body)
      setCorpora((prev) => [...prev, c])
      setCorpusId(c.id)
      setDocsByCorpus((prev) => new Map(prev).set(c.id, []))
      return c
    },
    [],
  )

  const addDocument = useCallback(
    async (body: { slug: string; markdown: string; activate: boolean }) => {
      if (!corpusId) throw new Error('no_corpus_selected')
      const doc = await ingestMarkdown({
        corpus_id: corpusId,
        slug: body.slug,
        markdown: body.markdown,
        source_name: `${body.slug}.md`,
        activate: body.activate,
      })
      await loadDocs(corpusId)
      return doc
    },
    [corpusId, loadDocs],
  )

  const activate = useCallback(
    async (docId: string) => {
      const next = await activateDocument(docId)
      patchDoc(next)
      if (corpusId) await loadDocs(corpusId)
    },
    [patchDoc, corpusId, loadDocs],
  )

  const archive = useCallback(
    async (docId: string) => {
      const next = await archiveDocument(docId)
      patchDoc(next)
    },
    [patchDoc],
  )

  const value = useMemo<KnowledgeContextValue>(
    () => ({
      corpora,
      corpusId,
      docs,
      loading,
      pending,
      error,
      selectCorpus,
      refresh,
      getDoc,
      getStructureCached,
      fetchNode,
      addCorpus,
      addDocument,
      activate,
      archive,
    }),
    [
      corpora,
      corpusId,
      docs,
      loading,
      pending,
      error,
      selectCorpus,
      refresh,
      getDoc,
      getStructureCached,
      fetchNode,
      addCorpus,
      addDocument,
      activate,
      archive,
    ],
  )

  return <KnowledgeContext.Provider value={value}>{children}</KnowledgeContext.Provider>
}

export function useKnowledge(): KnowledgeContextValue {
  const ctx = useContext(KnowledgeContext)
  if (!ctx) throw new Error('useKnowledge requires KnowledgeProvider')
  return ctx
}
